<div class="table-container">
  <!-- Search -->
  <div
  *ngIf="enableSearch && data.length > 0"
  class=" flex justify-end"
>
  <div class="relative w-full sm:max-w-sm">
    <span
      class="absolute start-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
    >
      <fa-icon [icon]="faMagnifyingGlass" class="text-sm"></fa-icon>
    </span>

    <input
      type="text"
      [(ngModel)]="searchTerm"
      (input)="onSearchChange()"
      [placeholder]="searchPlaceholder | translate"
      class="w-full h-11 ps-11 pe-4 border border-gray-200 rounded-xl bg-white text-sm text-gray-700 shadow-sm outline-none transition-all placeholder:text-gray-400 focus:border-[#1E2A78] focus:ring-2 focus:ring-[#1E2A78]/10"
    />
  </div>
</div>

  <!-- Only the table scrolls sideways; search and pagination stay put -->
  <div class="table-wrapper">
  <table class="table">
    <!-- HEADER -->
    <thead class="table-head">
      <tr>
        <th *ngIf="selectable">
          <input
            type="checkbox"
            class="table-checkbox"
            [checked]="isAllSelected"
            (change)="toggleAll($event)"
          />
        </th>

        <th *ngFor="let col of columns" [class.text-end]="isRightAligned(col)" [style.width]="col.width || null">
          {{ col.label | translate }}
        </th>

        <th *ngIf="actions.length" class="text-center table-action-head">
          {{ 'COMMON.ACTION' | translate }}
        </th>
      </tr>
    </thead>

    <!-- BODY -->
    <tbody class="table-body">
      <!-- No Search Result -->
      <tr *ngIf="filteredData.length === 0">
        <td
          class="text-center py-8 text-gray-400"
          [attr.colspan]="columns.length + (selectable ? 1 : 0) + (actions.length ? 1 : 0)"
        >
          {{ 'COMMON.NO_MATCHING_RECORDS' | translate }}
        </td>
      </tr>

      <tr *ngFor="let row of paginatedData" class="table-row">
        <td *ngIf="selectable" class="table-cell-start">
          <input
            type="checkbox"
            class="table-checkbox"
            [checked]="isSelected(row)"
            (change)="toggleRow(row, $event)"
          />
        </td>

        <td
          *ngFor="let col of columns"
          class="table-cell"
          [class.text-end]="isRightAligned(col)"
        >
          <ng-container [ngSwitch]="col.type">
            <!-- BADGE/PILL -->
            <span
              *ngSwitchCase="'badge'"
              class="status-pill"
              [ngClass]="col.badgeMap ? col.badgeMap[row[col.key]] : ''"
            >
              {{ (col.map ? col.map[row[col.key]] : row[col.key]) | translate }}
            </span>

            <!-- MAP -->
            <span *ngSwitchCase="'map'">
              {{ (col.map ? col.map[row[col.key]] : row[col.key]) | translate }}
            </span>

            <!-- DATE -->
            <span *ngSwitchCase="'date'" dir="ltr">
              {{ row[col.key] | date: 'dd-MMM-yyyy' }}
            </span>

            <!-- CURRENCY -->
            <span *ngSwitchCase="'currency'" dir="ltr">
              {{ row[col.key] > 0 ? (row[col.key] | number:'1.0-0') : '-' }}
            </span>

            <!-- LINK (plain text for rows the column disables) -->
            <ng-container *ngSwitchCase="'link'">
              @if (col.linkDisabled?.(row)) {
                <span>{{ row[col.key] | translate }}</span>
              } @else {
                <a
                  href="javascript:void(0)"
                  class="text-indigo-600 hover:text-indigo-800 font-medium hover:underline"
                  (click)="col.onClick ? col.onClick(row) : null; $event.stopPropagation()"
                >
                  {{ row[col.key] | translate }}
                </a>
              }
            </ng-container>

            <!-- DEFAULT TEXT -->
            <span *ngSwitchDefault>
              {{ row[col.key] | translate }}
            </span>
          </ng-container>
        </td>

        <td *ngIf="actions.length" class="table-cell-end">
          <div class="table-actions">
            <ng-container *ngFor="let action of actions">
              <button
                class="action-btn cursor-pointer"
                [ngClass]="action.class"
                [attr.title]="action.tooltip ? (action.tooltip | translate) : null"
                (click)="action.onClick(row); $event.stopPropagation()"
                *ngIf="!shouldHide(action, row)"
              >
                <ng-container *ngIf="action.icon">
                  <img
                    *ngIf="isImagePath(action.icon)"
                    [src]="action.icon"
                    [ngClass]="action.iconSize || 'w-5 h-5'"
                    class="object-contain"
                  />

                  <fa-icon
                    *ngIf="!isImagePath(action.icon)"
                    [icon]="action.icon"
                    class="w-full h-full"
                  ></fa-icon>
                </ng-container>

                {{ action.label ? (action.label | translate) : '' }}
              </button>

              <span
                *ngIf="action.keepSpace && shouldHide(action, row)"
                class="action-btn invisible"
                [ngClass]="action.class"
                aria-hidden="true"
              ></span>
            </ng-container>
          </div>
        </td>
      </tr>
    </tbody>
  </table>
  </div>

  <!-- Pagination -->
  <div
    *ngIf="enablePagination && totalRecords > 0"
    class="table-pagination"
  >
    <span> {{ 'COMMON.SHOWING_RECORDS' | translate: { start: startRecord, end: endRecord, total: totalRecords } }} </span>

    <div class="table-pager">
      <button
        type="button"
        class="table-pager-btn"
        (click)="prevPage()"
        [disabled]="currentPage === 1"
      >
        {{ 'COMMON.PREVIOUS' | translate }}
      </button>

      <ng-container *ngFor="let page of pages">
        <span *ngIf="page === null" class="table-pager-btn table-pager-gap">…</span>

        <button
          *ngIf="page !== null"
          type="button"
          class="table-pager-btn"
          [class.is-active]="page === currentPage"
          [attr.aria-current]="page === currentPage ? 'page' : null"
          (click)="goToPage(page)"
        >
          {{ page }}
        </button>
      </ng-container>

      <button
        type="button"
        class="table-pager-btn"
        (click)="nextPage()"
        [disabled]="endRecord === totalRecords"
      >
        {{ 'COMMON.NEXT' | translate }}
      </button>
    </div>
  </div>
</div>